// Widget-specific helpers for loading different types of widget resources.
// These functions handle the fetching and processing of widget fragments
// with consistent error handling and callback patterns.

import { fetchFragment } from '../../helpers/fragment';

// Widget-specific helper to load the generic hero widget HTML fragment.
export function loadGenericHeroWidget(options: {
    communityUrl: string;
    targetElement: Element;
    title?: string;
    heroImage?: string;
    subtitle?: string;
    align?: 'left' | 'center';
    backgroundColor?: string;
    backgroundColorDisplay?: string;
    titleColor?: string;
    titleColorDisplay?: string;
    subtitleColor?: string;
    subtitleColorDisplay?: string;
    onSuccess?: (html: string) => void;
    onError?: (err: unknown) => void;
}): Promise<string | undefined> {
    const {
        communityUrl,
        targetElement,
        title = '',
        heroImage = '',
        subtitle = '',
        align = 'center',
        backgroundColor = '#ffffff',
        backgroundColorDisplay = 'none',
        titleColor,
        titleColorDisplay,
        subtitleColor,
        subtitleColorDisplay,
        onSuccess,
        onError,
    } = options;
    if (!communityUrl) return Promise.resolve(undefined); // caller contract requires this

    const config: any = {
        heroImage,
        title,
        backgroundColorDisplay,
        enablePicker: false,
        subtitle,
        backgroundColor,
        align,
        hideSpacingWithUpperWidget: true,
        paddingToTopAndBottom: false,
        activeSection: 'Content',
        selectedWidgetPersonalisationOption: '',
        selectedWidgetPersonalisationOptionName: 'All+roles',
        startDateTimestamp: null,
        finishDateTimestamp: null,
    };

    if (titleColorDisplay) config.titleColorDisplay = titleColorDisplay;
    if (typeof titleColor !== 'undefined') config.titleColor = titleColor;
    if (subtitleColorDisplay) config.subtitleColorDisplay = subtitleColorDisplay;
    if (typeof subtitleColor !== 'undefined') config.subtitleColor = subtitleColor;

    const configParam = encodeURIComponent(JSON.stringify(config));
    const titleParam = encodeURIComponent(title);
    const widgetUrl = `${communityUrl}/widget/render?type=generic_hero&title=${titleParam}&config=${configParam}`;

    return fetchFragment(widgetUrl, {
        targetElement,
        onSuccess: (html: string) => {
            onSuccess?.(html);
        },
        onError: (err: unknown) => {
            console.error('Failed to load generic hero widget:', err);
            onError?.(err);
        },
    });
}

// Widget-specific helper to load the mega menu widget HTML fragment.
export function loadMegaMenuWidget(options: {
    communityUrl: string;
    targetElement: Element;
    onSuccess?: (html: string) => void;
    onError?: (err: unknown) => void;
}): Promise<string | undefined> {
    const {
        communityUrl,
        targetElement,
        onSuccess,
        onError,
    } = options;
    
    if (!communityUrl) return Promise.resolve(undefined); // caller contract requires this

    const widgetUrl = `${communityUrl}/widget/header`;

    return fetchFragment(widgetUrl, {
        targetElement,
        onSuccess: (html: string) => {
            onSuccess?.(html);
        },
        onError: (err: unknown) => {
            console.error('Failed to load mega menu widget:', err);
            onError?.(err);
        },
    });
}

// Widget-specific helper to load widget resources (CSS and state) fragment.
export function loadWidgetResources(
    communityUrl: string,
    options: {
        targetElement: Element;
        onSuccess?: () => void;
        onError?: (err: unknown) => void;
    }
) {
    const { targetElement, onSuccess, onError } = options;

  return fetchFragment(`${communityUrl}/widget/resources`, {
    targetElement,
    preprocess: (elements) => {
      // Look for <style id="css-variables"> and rewrite selector for shadow DOM
      elements.forEach((el) => {
        if (el.tagName === 'STYLE' && (el as HTMLStyleElement).id === 'css-variables') {
          const styleEl = el as HTMLStyleElement;
          // Always target the shadow root host instead of html
          styleEl.textContent = styleEl.textContent?.replace(/html\s*\{/g, ':host {') || styleEl.textContent;
        }
      });

      // Create and inject a script element to patch inSidedData with missing properties
      const patchScript = document.createElement('script');
      patchScript.type = 'text/javascript';
      patchScript.id = 'inSidedData-patch';
      patchScript.textContent = `
        // Patch the existing window.inSidedData object to ensure all required
        // content properties exist. This runs per widget to handle timing.
        if (!window.inSidedData) {
          window.inSidedData = {};
        }
        if (!window.inSidedData.content) {
          window.inSidedData.content = {};
        }
        if (!window.inSidedData.content.category) {
          window.inSidedData.content.category = {};
        }
        if (!window.inSidedData.content.topic) {
          window.inSidedData.content.topic = {};
        }
        if (!window.inSidedData.content.post) {
          window.inSidedData.content.post = {};
        }
        if (!window.inSidedData.content.path) {
          window.inSidedData.content.path = {};
        }
      `;
      
      // Add the patch script to the end of the elements array so it runs after resources load
      elements.push(patchScript);
    },
    onSuccess: () => {
      onSuccess?.();
    },
        onError: (err: unknown) => {
            console.error('Failed to load widget resources:', err);
            onError?.(err);
        },
    });
}

// Widget-specific helper to load widget resources body (JS to initialize the Preact app) fragment.
export function loadWidgetResourcesBody(
    communityUrl: string,
    options: {
        targetElement: Element;
        onSuccess?: () => void;
        onError?: (err: unknown) => void;
    }
) {
    const { targetElement, onSuccess, onError } = options;

    return fetchFragment(`${communityUrl}/widget/resourcesBody`, {
        targetElement,
        onSuccess: () => {
            onSuccess?.();
        },
        onError: (err: unknown) => {
            console.error('Failed to load widget resourcesBody:', err);
            onError?.(err);
        },
    });
}